var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _KeyedOperationsQueue_instances, _KeyedOperationsQueue_operations, _KeyedOperationsQueue_isRunning, _KeyedOperationsQueue_pubSub, _KeyedOperationsQueue_config, _KeyedOperationsQueue_removeEmptyEntries;
import createPubSub from "@lazarentertainment/js-pubsub";
import { chain, defer, isFulfilled } from "./promiseUtils";
const abortSignal = Symbol("abort");
/**
 * A utility to perform async operations, in which operations are performed
 * serially for the same key `K`, but in parallel for different `K`.
 *
 * For example, suppose a user wishes to archive a resource "ABC", and
 * you add an operation into this queue. However, the user realizes quickly
 * that they've archived the wrong resource; they intended to archive
 * resource "XYZ". Using this utility, you can enqueue a restore operation
 * for "ABC", and an archive operation for "XYZ". The restore operation for
 * "ABC" is guaranteed to run after the original archive operation, while
 * the archive operation for "XYZ" is allowed to run in parallel. Thus,
 * you can be confident that these operations will complete in the right
 * order, regardless of the circumstances (degraded service state, for
 * example).
 *
 * A benefit possible with this approach is, if your operations
 * share the same failure semantics, you can listen for the `errors`
 * event and handle all your errors in one place.
 *
 * Although the type for `K` can be anything you want, it would
 * commonly be a unique identifier on your data model.
 */
export class KeyedOperationsQueue {
    constructor({ failEarly = true, autoStart = true, } = {}) {
        _KeyedOperationsQueue_instances.add(this);
        _KeyedOperationsQueue_operations.set(this, new Map());
        _KeyedOperationsQueue_isRunning.set(this, false);
        _KeyedOperationsQueue_pubSub.set(this, createPubSub());
        _KeyedOperationsQueue_config.set(this, void 0);
        this.subscribe = __classPrivateFieldGet(this, _KeyedOperationsQueue_pubSub, "f").subscribe;
        this.unsubscribe = __classPrivateFieldGet(this, _KeyedOperationsQueue_pubSub, "f").unsubscribe;
        __classPrivateFieldSet(this, _KeyedOperationsQueue_config, {
            failEarly,
            autoStart,
        }, "f");
    }
    enqueue(key, operation) {
        var _a;
        if (isOperation(key)) {
            return this.enqueue(Symbol("enqueue"), key);
        }
        requireDefinedOperation(operation);
        const previousSize = this.size;
        const deferred = defer();
        let isAborted = false;
        const getIsAborted = () => isAborted;
        const operationsForKey = (_a = __classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").get(key)) !== null && _a !== void 0 ? _a : [];
        const enqueuedOperation = {
            operation: () => __awaiter(this, void 0, void 0, function* () {
                if (isAborted) {
                    throw abortSignal;
                }
                const result = yield operation(getIsAborted);
                deferred.resolve({
                    type: "completed",
                    value: result,
                });
            }),
            abort: (propagate = true) => {
                var _a;
                if (isAborted) {
                    return;
                }
                isAborted = true;
                // We resolve instead of rejecting with an error, so that the caller
                // doesn't need to worry about uncaught promise rejections from
                // aborted operations, which occur easily with calls to abortAll(),
                // commonly invoked on cleanup.
                deferred.resolve({
                    type: "aborted",
                });
                const index = operationsForKey.indexOf(enqueuedOperation);
                if (index === -1) {
                    return;
                }
                operationsForKey.splice(index, 1);
                if (propagate) {
                    (_a = operationsForKey[index]) === null || _a === void 0 ? void 0 : _a.abort();
                }
            },
            isAborted: getIsAborted,
        };
        operationsForKey.push(enqueuedOperation);
        __classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").set(key, operationsForKey);
        if (__classPrivateFieldGet(this, _KeyedOperationsQueue_config, "f").autoStart) {
            this.resume();
        }
        else if (previousSize === 0) {
            __classPrivateFieldGet(this, _KeyedOperationsQueue_pubSub, "f").publish("hasOperations");
        }
        return [Promise.resolve().then(() => deferred), enqueuedOperation.abort];
    }
    enqueueAll(key, ...operations) {
        if (!operations.length) {
            return [
                Promise.resolve({ type: "completed", value: undefined }),
                () => { },
            ];
        }
        const enqueueTuples = operations.map((operation) => this.enqueue(key, operation));
        // We only need to wait for the last promise, since they
        // run sequentially.
        const [promise] = enqueueTuples[enqueueTuples.length - 1];
        // We only need to abort the first operation, because it will
        // abort the entire chain.
        const [, abort] = enqueueTuples[0];
        return [promise, abort];
    }
    get size() {
        return [...__classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").values()].flat().length;
    }
    get isRunning() {
        return __classPrivateFieldGet(this, _KeyedOperationsQueue_isRunning, "f");
    }
    abortAll() {
        ;
        [...__classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").values()].forEach(([operation]) => operation === null || operation === void 0 ? void 0 : operation.abort());
        console.assert(this.size === 0, "Expected size to be 0");
        __classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").clear();
    }
    resume() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isRunning) {
                return;
            }
            const { failEarly } = __classPrivateFieldGet(this, _KeyedOperationsQueue_config, "f");
            const errors = new Map();
            while (this.size && !errors.size) {
                __classPrivateFieldSet(this, _KeyedOperationsQueue_isRunning, true, "f");
                // eslint-disable-next-line no-await-in-loop
                const result = yield Promise.allSettled([...__classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").entries()].map(([key, operations]) => 
                // Chain all operations so that they do not run concurrently
                chain(operations, (operation) => {
                    // If `failEarly` is true, don't continue running
                    // operations if some parallel operation has failed.
                    // This allows us to avoid publishing a parade of errors
                    // with the same underlying cause.
                    if (errors.size && failEarly) {
                        return Promise.reject(abortSignal);
                    }
                    return Promise.resolve()
                        .then(operation.operation)
                        .then(() => {
                        if (operation.isAborted()) {
                            return;
                        }
                        // The operation completed, we can dequeue.
                        const dequeuedOperation = operations.shift();
                        console.assert(dequeuedOperation === operation, "Expected dequeuedOperation.operation === operation");
                    })
                        .catch((error) => {
                        if (error === abortSignal) {
                            // Operation was aborted, so we propagate this down the chain.
                            throw error;
                        }
                        errors.set(key, { error, abort: operation.abort });
                        throw abortSignal;
                    });
                }).catch((error) => {
                    // The only error we expect to receive here is `abortSignal`,
                    // which means:
                    //   - The operation threw `abortSignal`, or
                    //   - The operation failed, the error was caught, and will
                    //     be published, or
                    //   - Some parallel operation failed
                    console.assert(error === abortSignal, "Expected error === abortSignal");
                })));
                // This assertion makes sure that assertions above are not
                // caught and silently ignored.
                console.assert(result.every(isFulfilled), "Unexpected rejected result");
            }
            __classPrivateFieldGet(this, _KeyedOperationsQueue_instances, "m", _KeyedOperationsQueue_removeEmptyEntries).call(this);
            if (errors.size) {
                // This is deferred so calls to `resume` don't stack
                setTimeout(() => {
                    // `errors` may contain multiple errors with the same underlying
                    // cause. Nonetheless we publish all errors. The caller can
                    // deduplicate and determine handling.
                    __classPrivateFieldGet(this, _KeyedOperationsQueue_pubSub, "f").publish("errors", errors);
                }, 0);
            }
            __classPrivateFieldSet(this, _KeyedOperationsQueue_isRunning, false, "f");
        });
    }
}
_KeyedOperationsQueue_operations = new WeakMap(), _KeyedOperationsQueue_isRunning = new WeakMap(), _KeyedOperationsQueue_pubSub = new WeakMap(), _KeyedOperationsQueue_config = new WeakMap(), _KeyedOperationsQueue_instances = new WeakSet(), _KeyedOperationsQueue_removeEmptyEntries = function _KeyedOperationsQueue_removeEmptyEntries() {
    ;
    [...__classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").entries()].forEach(([key, operationsForKey]) => {
        if (operationsForKey.length === 0) {
            __classPrivateFieldGet(this, _KeyedOperationsQueue_operations, "f").delete(key);
        }
    });
};
export function getErrors(errors) {
    return [...errors.values()].map(({ error }) => error);
}
function isOperation(arg) {
    return typeof arg === "function";
}
function requireDefinedOperation(operation) {
    console.assert(operation, "Expected operation to be defined");
}
